/**
 * 동기화 성공 타임라인
 * 세로 타임라인 + 타입별 아이콘 마커 + 컴팩트 카드
 */

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  ClipboardCheck,
  BarChart3,
  Camera,
  Settings2,
  Wrench,
  Ruler,
  FolderOpen,
} from "lucide-react";
import type { SyncQueueItem } from "@/lib/db/types";
import type { SyncItemNames } from "@/hooks/useSyncItemNames";
import type { EntityType } from "@/types";
import { getActionLabel, summarizePayload } from "@/lib/sync-labels";
import { getLocalPhoto } from "@/lib/db/photos";

/** 컨텍스트 레벨 */
type SyncLevel = "project" | "equipment";

const LEVEL_THEME: Record<SyncLevel, { dot: string; bg: string; text: string; border: string }> = {
  project: {
    dot: "bg-emerald-500",
    bg: "bg-emerald-50",
    text: "text-emerald-800",
    border: "border-emerald-200/60",
  },
  equipment: {
    dot: "bg-sky-500",
    bg: "bg-sky-50",
    text: "text-sky-800",
    border: "border-sky-200/60",
  },
};

/** 타입별 아이콘 + 컬러 */
const TYPE_THEME: Record<
  EntityType,
  {
    icon: React.FC<{ className?: string; size?: number }>;
    bg: string;
    text: string;
    ring: string;
  }
> = {
  checklist: {
    icon: ClipboardCheck,
    bg: "bg-blue-100",
    text: "text-blue-600",
    ring: "ring-blue-200",
  },
  data: { icon: BarChart3, bg: "bg-violet-100", text: "text-violet-600", ring: "ring-violet-200" },
  photo: { icon: Camera, bg: "bg-amber-100", text: "text-amber-600", ring: "ring-amber-200" },
  machine: { icon: Settings2, bg: "bg-slate-200", text: "text-slate-600", ring: "ring-slate-300" },
  equipment: { icon: Wrench, bg: "bg-teal-100", text: "text-teal-600", ring: "ring-teal-200" },
  measure: { icon: Ruler, bg: "bg-indigo-100", text: "text-indigo-600", ring: "ring-indigo-200" },
  project: {
    icon: FolderOpen,
    bg: "bg-emerald-100",
    text: "text-emerald-600",
    ring: "ring-emerald-200",
  },
};

/** 타임스탬프 → "14:30" */
function formatTime(ts: number): string {
  const d = new Date(ts);
  return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
}

/** 날짜 라벨: 오늘/어제/M월 D일 */
function formatDateLabel(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = today.getTime() - target.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) return "오늘";
  if (days === 1) return "어제";
  return `${d.getMonth() + 1}월 ${d.getDate()}일`;
}

// ─── 사진 썸네일 컴포넌트 ───

function PhotoThumbnail({ item }: { item: SyncQueueItem }) {
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (item.type !== "photo") return;
    const photoId = (item.payload.photoId as string) || item.entityId;
    if (!photoId) return;
    let cancelled = false;
    getLocalPhoto(photoId).then((photo) => {
      if (!cancelled && photo?.base64Thumbnail) setThumbnail(photo.base64Thumbnail);
    });
    return () => {
      cancelled = true;
    };
  }, [item.type, item.payload.photoId, item.entityId]);

  if (!thumbnail) return null;

  return (
    <>
      <button className="flex-shrink-0" onClick={() => setShowPreview(true)}>
        <img
          src={thumbnail}
          alt=""
          className="w-10 h-10 rounded-lg object-cover ring-1 ring-slate-200"
        />
      </button>
      {showPreview &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black/90"
            onClick={() => setShowPreview(false)}
          >
            <img
              src={thumbnail}
              alt=""
              className="max-h-[80vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-0 left-0 right-0 pb-safe">
              <div className="flex justify-center pb-8">
                <button
                  onClick={() => setShowPreview(false)}
                  className="flex items-center justify-center gap-2 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-8 py-3.5 text-sm font-semibold text-white active:scale-[0.98] active:bg-white/20 transition-all duration-150"
                >
                  <span className="text-lg">&times;</span>
                  닫기
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

// ─── 타임라인 항목 ───

interface TimelineItemProps {
  item: SyncQueueItem;
  names: SyncItemNames;
  isLast: boolean;
}

function TimelineItem({ item, names, isLast }: TimelineItemProps) {
  const theme = TYPE_THEME[item.type];
  const Icon = theme.icon;
  const ts = item.syncedAt ?? item.createdAt;

  const actionLabel = getActionLabel(item.type, item.operation, item.payload);
  const changesSummary = item.type === "photo" ? "" : summarizePayload(item.payload, item.type);

  // 컨텍스트 라벨 조합
  const projectName = item.projectId ? names.projectNames.get(item.projectId) : undefined;
  const machineName = item.machineId ? names.machineNames.get(item.machineId) : undefined;
  let machineLabel: string | null = null;
  if (machineName) {
    machineLabel = machineName;
    if (item.docKey) {
      const match = item.docKey.match(/^doc-(\d+)$/);
      if (match) machineLabel = `${machineName} #${match[1]}`;
    }
  }

  // 레벨별 뱃지 파츠
  type BadgePart = { label: string; level: SyncLevel };
  const badgeParts: BadgePart[] = [];
  if (projectName) badgeParts.push({ label: projectName, level: "project" });
  if (machineLabel) badgeParts.push({ label: machineLabel, level: "equipment" });
  const hasContext = badgeParts.length > 0;

  return (
    <div className="flex gap-3">
      {/* 타임라인 세로축: 아이콘 마커 + 연결선 */}
      <div className="flex flex-col items-center flex-shrink-0 w-9">
        <div
          className={`flex h-9 w-9 items-center justify-center rounded-full ${theme.bg} ${theme.text} ring-2 ${theme.ring} ring-offset-1 ring-offset-slate-50`}
        >
          <Icon size={16} />
        </div>
        {!isLast && <div className="w-px flex-1 bg-slate-200 my-1" />}
      </div>

      {/* 카드 콘텐츠 */}
      <div className="flex-1 min-w-0 pb-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {/* 메인 타이틀: 각 파트를 레벨 색상 뱃지로 */}
            {hasContext ? (
              <div className="flex items-center gap-1.5 flex-wrap">
                {badgeParts.map((bp, i) => {
                  const lt = LEVEL_THEME[bp.level];
                  return (
                    <span
                      key={i}
                      className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-[3px] text-[11px] font-semibold leading-none ${lt.bg} ${lt.text} ${lt.border}`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${lt.dot}`} />
                      {bp.label}
                    </span>
                  );
                })}
              </div>
            ) : (
              <p className="text-[13px] font-bold text-slate-900 truncate leading-snug">
                {actionLabel}
              </p>
            )}

            {/* 변경 내용 */}
            {changesSummary && (
              <p className="text-[11px] mt-1 whitespace-pre-line font-mono text-slate-400 leading-relaxed">
                {changesSummary}
              </p>
            )}

            {/* 시간 (제일 아래) */}
            <span className="text-[10px] text-slate-400 tabular-nums mt-0.5 block">
              {formatTime(ts)}
            </span>
          </div>

          {/* 우측: 액션 뱃지 + 사진 썸네일 */}
          <div className="flex items-center gap-2 flex-shrink-0 pt-0.5">
            {hasContext && (
              <span className="inline-flex items-center rounded-md border border-slate-200 bg-white px-1.5 py-[3px] text-[10px] font-medium text-slate-500 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                {actionLabel}
              </span>
            )}
            {item.type === "photo" && <PhotoThumbnail item={item} />}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── 메인 타임라인 ───

interface SyncTimelineProps {
  items: SyncQueueItem[];
  names: SyncItemNames;
}

export function SyncTimeline({ items, names }: SyncTimelineProps) {
  const dateBreaks = useMemo(() => {
    let last = "";
    return items.map((item) => {
      const ts = item.syncedAt ?? item.createdAt;
      const label = formatDateLabel(ts);
      const show = label !== last;
      last = label;
      return { showDate: show, dateLabel: label };
    });
  }, [items]);

  return (
    <div>
      {items.map((item, idx) => {
        const { showDate, dateLabel } = dateBreaks[idx];

        return (
          <div key={item.id}>
            {showDate && (
              <div className="flex items-center gap-3 py-2.5 mt-1 first:mt-0">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="text-[11px] font-bold text-slate-400 tracking-wider uppercase">
                  {dateLabel}
                </span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
            )}
            <TimelineItem item={item} names={names} isLast={idx === items.length - 1} />
          </div>
        );
      })}
    </div>
  );
}
